import { Select, SelectProps, useMantineTheme } from '@mantine/core';
import clsx from 'clsx';
import { useUnit } from 'effector-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistryModel, Row } from 'shared/lib';

export const REGISTRY_LIMIT_SELECT_DATA = [10, 25, 50];

export type RegistryPaginationLimitSelectProps = Omit<SelectProps, 'clearable'>;

export const createRegistryPaginationLimitSelect = <
	TRow extends Row,
	TFilters extends Record<string, unknown>,
	TError,
	TAdditionalData = unknown,
>(
	model: RegistryModel<TRow, TFilters, TError, TAdditionalData>,
) => {
	const RegistryPaginationLimitSelect = forwardRef<
		HTMLInputElement,
		RegistryPaginationLimitSelectProps
	>(({ className, ...props }, ref) => {
		const { t } = useTranslation();
		const { components } = useMantineTheme();

		const [limit, total, setLimit] = useUnit([
			model.limit.$value,
			model.$total,
			model.limit.setValue,
		]);

		const selectData = Array.from(new Set([...REGISTRY_LIMIT_SELECT_DATA, limit]))
			.sort((a, b) => a - b)
			.map(String);

		return (
			total > REGISTRY_LIMIT_SELECT_DATA[0] && (
				<Select
					ref={ref}
					data-test-id='registry-pagination-limit-select'
					label={t('registry.pagination-limit-select')}
					data={selectData}
					value={limit.toString()}
					clearable={false}
					className={clsx('registry-pagination-limit-select', className)}
					comboboxProps={{
						...components.Select?.defaultProps?.comboboxProps,
						position: 'top',
					}}
					onChange={(value) => setLimit(Number(value))}
					{...props}
				/>
			)
		);
	});

	return RegistryPaginationLimitSelect;
};
